import React, { useEffect, useState } from 'react';
import { Space, Button, Table, Modal, Input, Loader, Form, openNotification } from 'tt-ui-lib/core';
import { AddIcon, EditIcon, LitterBinIcon, ImageIcon, ArrowReloadIcon } from 'tt-ui-lib/icons';
import styles from '../Modals.module.scss';
import { imageFileTypes } from 'constants/addPost';
import axios from 'axios';
import { v1 as uuidv1 } from 'uuid';
import {
  deleteTemplatesSettings,
  getTemplateFileUploadLink,
  saveTemplatesSettings,
} from 'api/admin/endpoints/mailing';

export const SocialMediaTab = ({ data, onUpdate, categories }) => {
  const [form] = Form.useForm();

  const [addNewModal, setAddNewModal] = useState(false);
  const [image, setImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const onSubmit = (values) => {
    const key = isEdit
      ? editData?.key
      : `footer_social_${values.title?.toLowerCase() || 'unnamed'}_link`;
    const category = categories.find((c) => c.name === 'Social');

    if (!image?.uuid) {
      openNotification({
        message: 'Please upload an icon',
        type: 'info',
      });
      return;
    }

    const newData = {
      [`settings[${key}][title]`]: values.title,
      [`settings[${key}][value]`]: values.value,
      [`settings[${key}][icon]`]: image?.uuid || '',
      [`settings[${key}][category_id]`]: category?.id || '',
    };

    setEditLoading(true);

    saveTemplatesSettings(newData)
      .then(() => {
        setAddNewModal(false);
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        openNotification({
          message: 'Failed to save setting',
          type: 'error',
        });
      })
      .finally(() => {
        setEditLoading(false);
      });
  };

  const onDelete = (key) => {
    setDeleteLoading(true);

    deleteTemplatesSettings(key)
      .then(() => {
        setDeleteModal(false);
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        openNotification({ message: 'Failed to delete setting', type: 'error' });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const columns = [
    {
      title: 'Icon',
      dataIndex: 'icon_full',
      key: 'icon_full',
      render: (value) => <img src={value} alt="" style={{ width: 30, height: 30 }} />,
    },
    { title: 'Name', dataIndex: 'title', key: 'title' },
    { title: 'URL', dataIndex: 'value', key: 'value' },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="icon"
            icon={<EditIcon className="icon-blue-fill" style={{ width: 20, height: 20 }} />}
            onClick={() => {
              setIsEdit(true);
              setEditData(record);
              form.setFields([
                { name: 'title', value: record.title },
                { name: 'value', value: record.value },
              ]);
              setImage({
                file: null,
                uuid: record.icon,
                url: record.icon_full,
                loading: false,
                fileType: 'image',
                preview: record.icon_full,
              });
              setAddNewModal(true);
            }}
          />
          <Button
            type="icon"
            icon={<LitterBinIcon className="icon-blue-fill" style={{ width: 20, height: 20 }} />}
            onClick={() => {
              setDeleteModal(true);
              setDeleteId(record.key);
            }}
          />
        </Space>
      ),
    },
  ];

  const uploadImage = (file) => {
    setImageLoading(true);

    const fileName = `${uuidv1()}.png`;
    const fileType = 'image';

    getTemplateFileUploadLink(fileName)
      .then((response) => {
        const urlToUpload = response.data?.data?.url;

        if (urlToUpload) {
          axios
            .put(urlToUpload, file, {
              headers: {
                'Content-type': file.type,
                'Access-Control-Allow-Origin': '*',
              },
            })
            .then((uploadResponse) => {
              if (uploadResponse.statusText === 'OK') {
                const updatedFile = {
                  file: file,
                  uuid: fileName,
                  url: `${'https://'}${response.data?.data?.download_url}`,
                  loading: false,
                  fileType: fileType,
                  preview: URL.createObjectURL(file),
                };

                setImage(updatedFile);
                setImageLoading(false);
              }
            })
            .catch(() => {
              setImageLoading(false);
            });
        } else {
          setImageLoading(false);
        }
      })
      .catch(() => {
        setImageLoading(false);
      });
  };

  useEffect(() => {
    if (!deleteModal) {
      setDeleteId(null);
    }
  }, [deleteModal]);

  useEffect(() => {
    if (!addNewModal) {
      form.resetFields();
      setEditData(null);
      setImage(null);
    }
  }, [addNewModal]);

  return (
    <Space direction="vertical" style={{ width: '100%', marginTop: 16, gap: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<AddIcon className="icon-white-fill" style={{ width: 20, height: 20 }} />}
          onClick={() => setAddNewModal(true)}
        >
          Add new
        </Button>
      </Space>

      <Table columns={columns} dataSource={data?.Social || []} />

      <Modal open={addNewModal} onClose={() => setAddNewModal(false)} title="Add new">
        <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
              <Form.Item name="title" rules={[{ required: true, message: 'Please enter a title' }]}>
                <Input placeholder="Name" />
              </Form.Item>
              <Form.Item
                name="value"
                rules={[
                  { required: true, message: 'Please enter URL' },
                  { type: 'url', message: 'Please enter a valid URL' },
                ]}
              >
                <Input placeholder="URL" />
              </Form.Item>
            </Space>
          </Form>

          <Space direction="vertical" style={{ width: '100%', gap: 8 }}>
            <b style={{ fontWeight: 500 }}>Icon</b>

            <input
              id="icon-template-upload"
              type="file"
              hidden
              onChange={(e) => {
                setImage(null);
                uploadImage(e.target.files[0]);
              }}
              accept={imageFileTypes}
            />
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <label
              htmlFor="icon-template-upload"
              style={{ width: '100%', display: 'block' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.dndCoverContent}>
                {image ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div className={styles.formImageWrapper}>
                      <Space style={{ alignItems: 'flex-start' }}>
                        <img
                          src={image.preview}
                          alt=""
                          style={{
                            height: '100%',
                            width: '50px',
                            objectFit: 'cover',
                          }}
                        />

                        <div style={{ fontWeight: 500 }}>
                          {image?.file?.name || image?.uuid || ''}
                        </div>
                      </Space>

                      <div>
                        <Space style={{ gap: 16 }}>
                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              document.getElementById('icon-template-upload').click();
                            }}
                          >
                            <ArrowReloadIcon style={{ width: 24, height: 24 }} />
                          </div>

                          <div
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setImage(null);
                            }}
                          >
                            <LitterBinIcon style={{ width: 24, height: 24 }} />
                          </div>
                        </Space>
                      </div>
                    </div>
                  </Space>
                ) : imageLoading ? (
                  <div className={styles.formImageLoader}>
                    <Loader />
                  </div>
                ) : (
                  <>
                    <Button
                      type="link"
                      icon={
                        <AddIcon
                          style={{ width: '20px', height: '20px' }}
                          className="icon-blue-fill"
                        />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        document.getElementById('icon-template-upload').click();
                      }}
                    >
                      Add icon
                    </Button>
                  </>
                )}
              </div>
            </label>
          </Space>

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button type="default" onClick={() => setAddNewModal(false)}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={editLoading}>
              Save
            </Button>
          </Space>
        </Space>
      </Modal>

      <Modal
        title="Are you sure you want to delete this item?"
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 10 }}>
          <Button type="default" onClick={() => setAddNewModal(false)}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => onDelete(deleteId)} loading={deleteLoading}>
            Delete
          </Button>
        </Space>
      </Modal>
    </Space>
  );
};

export const SectionsTab = ({ data, onUpdate, categories }) => {
  const [form] = Form.useForm();

  const [addNewModal, setAddNewModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const onSubmit = (values) => {
    const key = isEdit
      ? editData?.key
      : `footer_link_${values.name?.toLowerCase() || 'unnamed'}_url`;
    const category = categories.find((c) => c.name === 'Sections');

    const newData = {
      [`settings[${key}][title]`]: values.name,
      [`settings[${key}][value]`]: values.value,
      [`settings[${key}][category_id]`]: category?.id || '',
    };

    setEditLoading(true);

    saveTemplatesSettings(newData)
      .then(() => {
        setAddNewModal(false);
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        openNotification({
          message: 'Failed to save setting',
          type: 'error',
        });
      })
      .finally(() => {
        setEditLoading(false);
      });
  };

  const onDelete = (key) => {
    setDeleteLoading(true);

    deleteTemplatesSettings(key)
      .then(() => {
        setDeleteModal(false);
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        openNotification({ message: 'Failed to delete setting', type: 'error' });
      });
  };

  const columns = [
    { title: 'Section title', dataIndex: 'title', key: 'title' },
    { title: 'URL', dataIndex: 'value', key: 'value' },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="icon"
            icon={<EditIcon className="icon-blue-fill" style={{ width: 20, height: 20 }} />}
            onClick={() => {
              setIsEdit(true);
              setEditData(record);
              form.setFields([
                { name: 'name', value: record.title },
                { name: 'value', value: record.value },
              ]);
              setAddNewModal(true);
            }}
          />
          <Button
            type="icon"
            icon={<LitterBinIcon className="icon-blue-fill" style={{ width: 20, height: 20 }} />}
            onClick={() => {
              setDeleteModal(true);
              setDeleteId(record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!deleteModal) {
      setDeleteId(null);
    }
  }, [deleteModal]);

  useEffect(() => {
    if (!addNewModal) {
      form.resetFields();
      setEditData(null);
    }
  }, [addNewModal]);

  return (
    <Space direction="vertical" style={{ width: '100%', marginTop: 16, gap: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<AddIcon className="icon-white-fill" style={{ width: 20, height: 20 }} />}
          onClick={() => setAddNewModal(true)}
        >
          Add new
        </Button>
      </Space>

      <Table columns={columns} dataSource={data?.Sections || []} />

      <Modal open={addNewModal} onClose={() => setAddNewModal(false)} title="Add new">
        <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
              <Form.Item name="name" rules={[{ required: true, message: 'Please enter a title' }]}>
                <Input placeholder="Name" />
              </Form.Item>
              <Form.Item name="value" rules={[{ required: true, message: 'Please enter URL' }]}>
                <Input placeholder="URL" />
              </Form.Item>
            </Space>
          </Form>

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button type="default" onClick={() => setAddNewModal(false)}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={editLoading}>
              Save
            </Button>
          </Space>
        </Space>
      </Modal>

      <Modal
        title="Are you sure you want to delete this item?"
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
      >
        <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 10 }}>
          <Button type="default" onClick={() => setAddNewModal(false)}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => onDelete(deleteId)} loading={deleteLoading}>
            Delete
          </Button>
        </Space>
      </Modal>
    </Space>
  );
};

export const PhotoTab = ({ data }) => {
  const [image, setImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const deletePhoto = () => {
    setImageLoading(true);

    const imgData = {
      [`settings[header_logo_default][value]`]: '',
    };

    saveTemplatesSettings(imgData)
      .then(() => {
        setImage(null);
      })
      .catch(() => {
        openNotification({
          message: 'Failed to delete image',
          type: 'error',
        });
      })
      .finally(() => {
        setImageLoading(false);
      });
  };

  const addNewPhoto = (file) => {
    setImageLoading(true);

    const fileName = `${uuidv1()}.png`;
    const fileType = 'image';

    getTemplateFileUploadLink(fileName)
      .then((response) => {
        const urlToUpload = response.data?.data?.url;

        if (urlToUpload) {
          axios
            .put(urlToUpload, file, {
              headers: {
                'Content-type': file.type,
                'Access-Control-Allow-Origin': '*',
              },
            })
            .then((uploadResponse) => {
              if (uploadResponse.statusText === 'OK') {
                const updatedFile = {
                  file: file,
                  uuid: fileName,
                  url: `${'https://'}${response.data?.data?.download_url}`,
                  loading: false,
                  fileType: fileType,
                  preview: URL.createObjectURL(file),
                };

                const imgData = {
                  [`settings[header_logo_default][value]`]: updatedFile.uuid,
                };

                saveTemplatesSettings(imgData)
                  .then(() => {
                    setImage(updatedFile);
                  })
                  .catch(() => {
                    openNotification({
                      message: 'Failed to save new image',
                      type: 'error',
                    });
                  })
                  .finally(() => {
                    setImageLoading(false);
                  });
              }
            })
            .catch(() => {
              setImageLoading(false);
            });
        } else {
          setImageLoading(false);
        }
      })
      .catch(() => {
        setImageLoading(false);
      });
  };

  useEffect(() => {
    if (data?.Photo[0]?.value) {
      setImage({
        file: null,
        uuid: data?.Photo[0]?.value,
        url: data?.Photo[0]?.value,
        loading: false,
        fileType: 'image',
        preview: data?.Photo[0]?.value,
      });
    }
  }, [data]);

  return (
    <Space direction="vertical" style={{ width: '100%', marginTop: 16, gap: 16 }}>
      <div>
        <input
          id="cover-template-upload"
          type="file"
          hidden
          onChange={(e) => {
            setImage(null);
            addNewPhoto(e.target.files[0]);
          }}
          accept={imageFileTypes}
        />
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <label
          htmlFor="cover-template-upload"
          style={{ width: '100%', display: 'block' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.dndCoverContent}>
            {image ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div className={styles.settingsPhotoWrapper}>
                  <img
                    src={image.preview}
                    alt=""
                    style={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                    }}
                  />

                  <div
                    style={{
                      cursor: 'pointer',
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      padding: 5,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: 10,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePhoto();
                    }}
                  >
                    <LitterBinIcon style={{ width: 18, height: 18 }} className="icon-white-fill" />
                  </div>
                </div>

                <Button
                  type="primary"
                  icon={<ImageIcon className="icon-white-fill" style={{ width: 20, height: 20 }} />}
                  style={{ width: '100%', maxWidth: '100%' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    document.getElementById('cover-template-upload').click();
                  }}
                >
                  Upload new
                </Button>
              </Space>
            ) : imageLoading ? (
              <div className={styles.formImageLoader}>
                <Loader />
              </div>
            ) : (
              <>
                <Button
                  type="primary"
                  icon={<ImageIcon className="icon-white-fill" style={{ width: 20, height: 20 }} />}
                  style={{ width: '100%', maxWidth: '100%' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    document.getElementById('image-template-upload').click();
                  }}
                >
                  Upload new
                </Button>
              </>
            )}
          </div>
        </label>
      </div>
    </Space>
  );
};
